<template>
    <div class="border-border p-6 rounded-xl flex flex-col justify-between" :class="[isEditingMode ? 'border-dashed border-4 bg-surface-sunken' : 'border-2 bg-surface-raised']">
        <div class="mb-3 md:mb-6 flex gap-3 md:gap-6 items-start">
            <slot name="icon"></slot>
            <h3 class="title-subsection grow">{{ questionText }}</h3>
            <BaseTooltip v-if="evidenceMessages.length > 0" :as-child="false" :trigger-aria-label="t('profile.questions.answer.howLearnedA11yLabel')">
                <template #trigger>
                    <BaseButton theme="neutral" icon-only class="hidden md:inline-flex" :aria-label="t('profile.questions.answer.learnedFromA11yLabel')">
                        <i class="bi bi-question-circle" aria-hidden="true"></i>
                    </BaseButton>
                </template>
                <template #content>
                    <div class="max-w-80">{{ tooltipText }}</div>
                </template>
            </BaseTooltip>
        </div>
        <div class="rounded-xl px-6 py-3 focus-field-within" :class="[isEditingMode ? 'bg-control-bg border-2 border-control-border' : 'bg-surface-sunken']">
            <BaseTextarea
                v-model:input="input"
                class="w-full my-2"
                :placeholder="placeholder ?? t('profile.questions.answer.placeholder')"
                :disabled="!isEditingMode"
                :aria-label="t('profile.questions.answer.textareaA11yLabel', { question: questionText })"
                @keydown.enter="handleSave" />
            <div class="flex gap-4 justify-between items-center">
                <BaseTooltip v-if="evidenceMessages.length > 0" :as-child="false" :trigger-aria-label="t('profile.questions.answer.howLearnedA11yLabel')">
                    <template #trigger>
                        <BaseButton theme="neutral" icon-only class="md:hidden" :aria-label="t('profile.questions.answer.learnedFromA11yLabel')">
                            <i class="bi bi-question-circle" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                    <template #content>
                        <div class="max-w-80">{{ tooltipText }}</div>
                    </template>
                </BaseTooltip>
                <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss class="py-2" />
                <div class="flex gap-3 ml-auto items-center">
                    <LoadingSpinner v-if="isCreateFetching || isUpdateFetching || isDeleteFetching" class="w-6 h-6" />
                    <template v-else-if="isEditingMode">
                        <BaseButton
                            v-if="!isTranscribing"
                            :theme="isRecording ? 'primary' : 'tertiary'"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="recordButtonLabel"
                            :aria-pressed="isRecording"
                            :title="recordButtonLabel"
                            @click="handleRecord">
                            <i class="bi" :class="isRecording ? 'bi-stop-circle' : 'bi-mic'" aria-hidden="true"></i>
                        </BaseButton>
                        <LoadingSpinner v-else class="w-6 h-6" />
                        <BaseButton theme="secondary" :disabled="!input || isDisabled" @click="handleSave"> {{ t("common.save") }} </BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="t('profile.questions.answer.editA11yLabel', { question: questionText })"
                            :title="t('profile.questions.answer.editTitle')"
                            @click="handleEdit">
                            <i class="bi bi-pencil text-xl/none" aria-hidden="true"></i>
                        </BaseButton>
                        <BaseButton
                            theme="tertiary"
                            size="sm"
                            icon-only
                            :disabled="isDisabled"
                            :aria-label="t('profile.questions.answer.deleteA11yLabel', { question: questionText })"
                            :title="t('profile.questions.answer.deleteTitle')"
                            @click="isConfirmDeleteDialogOpen = true">
                            <i class="bi bi-trash text-xl/none" aria-hidden="true"></i>
                        </BaseButton>
                    </template>
                </div>
            </div>
        </div>
        <ConfirmationDialog
            v-model:open="isConfirmDeleteDialogOpen"
            :title="t('profile.questions.answer.deleteDialog.title')"
            :text="t('profile.questions.answer.deleteDialog.text')"
            :confirm-label="t('common.delete')"
            @confirm="deleteExecute" />
    </div>
</template>

<script setup>
import { transcribeRecording } from "/js/transcription.js";
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import { useMicConsent } from "~vue/composables/useMicConsent";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { isImeComposing } from "~vue/utils/keyboard";
import { logError } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { question, recorder, isEditing } = defineProps({
    question: { type: Object, required: true },
    // Unset falls back to the translated default in the template; a prop default can't call t().
    placeholder: { type: String, default: undefined },
    isEditing: { type: Boolean, default: false }, // used to set initial state of editing mode
    isDisabled: { type: Boolean, default: false },
    recorder: { type: Object, required: true },
});
const emit = defineEmits(["edit", "permission-open", "permission-close", "record-start", "record-end"]);

const isEditingMode = ref(isEditing);

const questionText = computed(() => (isEditing ? question.question : (question.heading ?? question.key_display)));
const evidenceMessages = computed(() => question.evidence_messages.map(({ message }) => message).join(", "));
const tooltipText = computed(() => t("profile.questions.answer.learnedFrom", { evidence: evidenceMessages.value }));

const input = ref(question.answer);
const error = ref(null);
const isConfirmDeleteDialogOpen = ref(false);

watch(input, () => {
    if (error.value) {
        error.value = null;
    }
});

const isRecording = ref(false);
const isTranscribing = ref(false);
const recordButtonLabel = computed(() => (isRecording.value ? t("profile.questions.answer.stopRecording") : t("profile.questions.answer.startRecording")));

watch(isRecording, (newValue) => {
    if (!newValue) {
        emit("record-end");
    }
});

const { micConsentBlocks } = useMicConsent();

const handleRecord = async () => {
    error.value = null;
    if (isRecording.value) {
        isRecording.value = false;
        recorder.stop();
    } else {
        try {
            if (recorder.isRecording) {
                return;
            }
            if (micConsentBlocks(handleRecord)) {
                return;
            }
            isRecording.value = true;
            recorder.on("finish", handleTranscribe);
            await recorder.start(async (micPermissionState) => {
                if (micPermissionState === "prompt") {
                    emit("permission-open");
                }
            });
            emit("record-start", { questionKey: question.key });
        } catch (e) {
            logError(`Failed to start recording: ${e}`);
            recorder.off("finish", handleTranscribe);
            isRecording.value = false;
            if (e.name === "NotAllowedError") {
                error.value = t("profile.questions.answer.errors.micPermission");
            } else {
                error.value = t("profile.questions.answer.errors.recordingFailed");
            }
        } finally {
            emit("permission-close");
        }
    }
};

const onError = (e) => {
    logError(`Failed to save answer: ${e}`);
    error.value = t("profile.questions.answer.errors.saveFailed");
};
const createPayload = ref({});
const {
    onFetchError: onCreateError,
    onFetchResponse: onCreateResponse,
    execute: createExecute,
    isFetching: isCreateFetching,
} = useFetch("/accounts/profile-data/", { immediate: false }).post(createPayload);
onCreateError((e) => {
    onError(e);
});
onCreateResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
});

const updatePayload = ref({});
const {
    onFetchError: onUpdateError,
    onFetchResponse: onUpdateResponse,
    execute: updateExecute,
    isFetching: isUpdateFetching,
} = useFetch(`/accounts/profile-data/${+question.id}`, { immediate: false }).put(updatePayload);
onUpdateError((e) => {
    onError(e);
});
onUpdateResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
    if (!isEditing) {
        isEditingMode.value = false;
    }
});

const {
    onFetchError: onDeleteError,
    onFetchResponse: onDeleteResponse,
    execute: deleteExecute,
    isFetching: isDeleteFetching,
} = useFetch(`/accounts/profile-data/${+question.id}`, { immediate: false }).delete();
onDeleteError((e) => {
    onError(e);
});
onDeleteResponse(() => {
    router.reload({ only: ["user_profile_entries"] });
});

const handleSave = (e) => {
    if (e instanceof KeyboardEvent && isImeComposing(e)) {
        return;
    }
    if (!e.shiftKey) {
        e.preventDefault();
        if (isEditingMode.value && !!question.id) {
            updatePayload.value = { value: input.value, profile_question_key: question.key_display };
            updateExecute();
        } else {
            createPayload.value = { profile_question_key: question.key_display, value: input.value };
            createExecute();
        }
    }
};

const handleEdit = () => {
    isEditingMode.value = true;
};

const handleTranscribe = async ({ blob }) => {
    isTranscribing.value = true;
    error.value = null;
    try {
        const transcript = await transcribeRecording({
            blob,
            onStart: () => {
                isTranscribing.value = true;
            },
            onError: (err) => {
                logError(err, {
                    context: { transcription: { source: "ProfileQuestionAnswer" } },
                    tags: { error_type: "transcription", error_code: err.error_code },
                });
            },
        });
        isTranscribing.value = false;
        input.value = transcript;
    } catch (e) {
        logError(e, {
            context: { transcription: { source: "ProfileQuestionAnswer" } },
            tags: { error_type: "transcription" },
        });
        isTranscribing.value = false;
        error.value = t("profile.questions.answer.errors.transcriptionFailed");
    } finally {
        recorder.off("finish", handleTranscribe);
        isRecording.value = false;
    }
};
</script>
